import React, { useEffect, useRef } from 'react';
import { useChat } from './hooks/useChat';
import MessageItem from './components/MessageItem';
import InputArea from './components/InputArea';
import InlineInput from './components/InlineInput';
import { motion, AnimatePresence } from 'framer-motion';

function App() {
  const { messages, isLoading, sendMessage, emailGated, submitEmail, emailSubmitted } = useChat();
  const appRef = useRef(null);

  // Only the initial AI greeting exists, no user messages yet
  const isInitialState = messages.length === 1 && messages[0].role === 'ai';

  // Observe height changes and send them to the parent window
  useEffect(() => {
    if (!appRef.current) return;

    const observer = new ResizeObserver(() => {
      if (appRef.current) {
        // Get the full scrollable height of the app container
        const height = appRef.current.scrollHeight;
        // Send the height to the parent window
        window.parent.postMessage({ type: 'iframe-resize', height }, '*');
      }
    });

    observer.observe(appRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={appRef} className="flex flex-col bg-white font-sans min-h-screen">
      <AnimatePresence mode="wait">
        {isInitialState ? (
          /* ── INITIAL STATE: greeting + inline underline input ── */
          <motion.main
            key="initial"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="flex-1 flex flex-col justify-center px-6 py-8"
          >
            <div className="max-w-xl w-full mx-auto">
              {/* Greeting text */}
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                className="text-[15px] text-gray-600 leading-relaxed mb-8"
              >
                {messages[0].content}
              </motion.p>

              {/* Inline underline input */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.4 }}
              >
                <InlineInput onSend={sendMessage} disabled={isLoading} />
              </motion.div>
            </div>
          </motion.main>
        ) : (
          /* ── CONVERSATION STATE ── */
          <motion.main
            key="chat"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="flex-1"
          >
            <div className="max-w-3xl mx-auto px-4 pt-6 pb-2">
              {messages.map((msg) => (
                <MessageItem key={msg.id} message={msg} />
              ))}
              
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start mb-5"
                >
                  <div className="flex gap-1.5 py-3 items-center">
                    {[0, 150, 300].map((delay, i) => (
                      <div
                        key={i}
                        className="w-2 h-2 rounded-full animate-bounce"
                        style={{
                          backgroundColor: '#543e18',
                          opacity: 0.5 + (i * 0.2),
                          animationDelay: `${delay}ms`
                        }}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.main>
        )}
      </AnimatePresence>

      {/* Bottom input — only shown in conversation state */}
      {!isInitialState && (
        <InputArea
          emailGated={emailGated}
          onSubmitEmail={submitEmail}
          emailSubmitted={emailSubmitted}
        />
      )}
    </div>
  );
}

export default App;